import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type IdentityProviderResource, IdentityProviderStatus } from '@today/types'
import type { AuthConfigSubmit, ProviderFormSubmit, RotateCertificateSubmit } from '@today/ui-domain'
import { useState } from 'react'
import { runSsoTestLogin } from '@/auth/sso-test-popup'
import { tenantAuthenticationService as service } from '@/services/tenant-authentication.service'
import { describeAuthenticationError } from './tenant-authentication-errors'

/** Which provider form is open, if any. */
export type ProviderEditor =
	| { mode: 'create' }
	| { mode: 'edit'; provider: IdentityProviderResource }
	| { mode: 'rotate'; provider: IdentityProviderResource }

/**
 * A provider may be the default, and is offered at sign-in, only when enabled,
 * verified and backed by a Cognito provider — SAML or OIDC (REQ-007, TO-378).
 */
export const isUsableProvider = (provider: IdentityProviderResource): boolean =>
	provider.enabled && provider.status === IdentityProviderStatus.VERIFIED && provider.cognitoProviderName !== null

const errorOf = (mutation: { isError: boolean; error: unknown }) =>
	mutation.isError ? describeAuthenticationError(mutation.error) : null

/** The create / edit submission as the API takes it: an edit never re-sends the immutable code or protocol. */
const submitProvider = (slug: string, editor: ProviderEditor | null, input: ProviderFormSubmit) => {
	if (editor?.mode === 'edit') {
		const { code: _code, protocol: _protocol, ...patch } = input
		return service.updateProvider(slug, editor.provider.id, patch)
	}
	if (!input.configuration) throw new Error('A new provider needs a configuration.')
	return service.createProvider(slug, { ...input, configuration: input.configuration })
}

/** The provider writes. Each refetches the view, since the default and the order may change. */
function useProviderMutations(slug: string, editor: ProviderEditor | null, close: () => void) {
	const client = useQueryClient()
	const refresh = () => client.invalidateQueries({ queryKey: ['tenant-authentication', slug] })
	const submit = useMutation({
		mutationFn: (input: ProviderFormSubmit) => submitProvider(slug, editor, input),
		onSuccess: async () => {
			close()
			await refresh()
		},
	})
	const rotate = useMutation({
		mutationFn: (input: RotateCertificateSubmit) =>
			editor?.mode === 'rotate'
				? service.rotateCertificate(slug, editor.provider.id, input)
				: Promise.reject(new Error('No provider is being rotated.')),
		onSuccess: async () => {
			close()
			await refresh()
		},
	})
	const toggle = useMutation({
		mutationFn: ({ provider, enabled }: { provider: IdentityProviderResource; enabled: boolean }) =>
			service.updateProvider(slug, provider.id, { enabled }),
		onSuccess: refresh,
	})
	const test = useMutation({
		mutationFn: async (provider: IdentityProviderResource) => {
			if (!provider.cognitoProviderName) throw new Error('This provider cannot run a test login yet.')
			const result = await service.testProvider(slug, provider.id, await runSsoTestLogin(provider.cognitoProviderName))
			return { ...result, name: provider.name }
		},
		onSuccess: refresh,
	})
	return { submit, rotate, toggle, test }
}

/**
 * The Identity & login page's logic (TO-410 design D12): load the tenant's
 * authentication config and providers, and drive the config save, the
 * provider create / edit / enable / rotate, and the per-provider test-login
 * popup. The same hook serves both planes; the server decides whether the
 * caller may address this tenant.
 */
export function useTenantAuthentication(slug: string) {
	const client = useQueryClient()
	const key = ['tenant-authentication', slug]
	const query = useQuery({ queryKey: key, queryFn: () => service.get(slug) })
	const [editor, setEditor] = useState<ProviderEditor | null>(null)
	const saveConfig = useMutation({
		mutationFn: (input: AuthConfigSubmit) => service.putConfig(slug, input),
		onSuccess: (view) => client.setQueryData(key, view),
	})
	const providers = useProviderMutations(slug, editor, () => setEditor(null))
	const all = [saveConfig, providers.submit, providers.rotate, providers.toggle, providers.test]
	const testResult = providers.test.data

	return {
		data: query.data ?? null,
		loadError: query.isError ? describeAuthenticationError(query.error) : null,
		usableProviders: (query.data?.providers ?? []).filter(isUsableProvider),
		isBusy: all.some((mutation) => mutation.isPending),
		configError: errorOf(saveConfig),
		listError: errorOf(providers.toggle) ?? errorOf(providers.test),
		editorError: errorOf(providers.submit) ?? errorOf(providers.rotate),
		testMessage: testResult ? `Test login ${testResult.passed ? 'passed' : 'failed'} for ${testResult.name}.` : null,
		editor,
		openEditor: (next: ProviderEditor) => {
			providers.submit.reset()
			providers.rotate.reset()
			setEditor(next)
		},
		closeEditor: () => setEditor(null),
		saveConfig: (input: AuthConfigSubmit) => saveConfig.mutate(input),
		submitProvider: (input: ProviderFormSubmit) => providers.submit.mutate(input),
		rotate: (input: RotateCertificateSubmit) => providers.rotate.mutate(input),
		toggleEnabled: (provider: IdentityProviderResource, enabled: boolean) =>
			providers.toggle.mutate({ provider, enabled }),
		test: (provider: IdentityProviderResource) => providers.test.mutate(provider),
	}
}
